import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EditorContent } from "@tiptap/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { FloatingPanelSeparator } from "@babackapp/ui/components/floating-panel";
import PanelContainer, {
  useBlockPanelPropsContext,
} from "../../lib/panel/panel-container";
import PanelContent from "../../lib/panel/panel-content";
import PanelTranslationState from "../../lib/panel/panel-translation-state";
import { EmailPanelToolbar } from "../paragraph/email-panel-toolbar";
import { BON_DE_RETOUR_CONTENT_KEY } from "../return-shipping-instructions/instructions";
import { useCarrierMessageOptions } from "../return-shipping-instructions/carrier-options";
import { useTitleEditor } from "./email-panel-editor";
import type { ReturnShippingInstructionsTitleBlockProps } from "./types";

/** Rich-text editor for the currently-selected carrier's title. */
function TitleEditor({ contentKey }: { contentKey: string }) {
  const editor = useTitleEditor(contentKey);
  return (
    <div className="space-y-2">
      <EmailPanelToolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}

function ReturnShippingInstructionsTitlePanelContent() {
  const { t } = useTranslation("editor-blocks-library");
  const { locale } = useBlockPanelPropsContext();
  // Same carrier list as the message block (+ the return slip).
  const options = useCarrierMessageOptions();
  const [selectedKey, setSelectedKey] = useState<string>(
    () => options[0]?.value ?? BON_DE_RETOUR_CONTENT_KEY,
  );

  return (
    <>
      <PanelContent
        title={t(
          "editorBlocksLibrary.blocks.return-shipping-instructions-title.panel.message.title",
          "Title to customize",
        )}
      >
        <Select value={selectedKey} onValueChange={setSelectedKey}>
          <SelectTrigger className="w-full h-8 text-sm bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PanelContent>
      <PanelContent
        title={t(
          "editorBlocksLibrary.blocks.return-shipping-instructions-title.panel.content.title",
          "Custom title",
        )}
      >
        {/* Remount on key OR locale change so the editor re-initializes both
            its content and its (locale-dependent) placeholder. */}
        <TitleEditor key={`${locale}:${selectedKey}`} contentKey={selectedKey} />
      </PanelContent>
      <FloatingPanelSeparator />
      <PanelTranslationState mode="translatable" portal />
    </>
  );
}

export function ReturnShippingInstructionsTitlePanelRenderer(
  blockProps: ReturnShippingInstructionsTitleBlockProps,
) {
  return (
    <PanelContainer blockProps={blockProps}>
      <ReturnShippingInstructionsTitlePanelContent />
    </PanelContainer>
  );
}
